import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { ChatMessage, ProactiveGreetingPage } from '@teo/widget-shared';
import {
  addOptimisticImageMessage,
  addOptimisticMessage,
  applyOutgoingStatusPipeline,
  confirmOptimisticMessage,
  createClientMessageId,
  createWidgetMessageId,
  enqueueOutboundMessage,
  findMessageByClientId,
  flushOutboundQueue,
  getOutboundImageBlob,
  getWidgetSession,
  isBrowserOnline,
  isImageQueueEntry,
  isNetworkError,
  isRetryableQueueError,
  logImage,
  logRealtime,
  logScroll,
  logSync,
  diffMessageLists,
  formatMessageListDiff,
  summarizeChatMessage,
  markMessageFailed,
  markMessageSending,
  markUserMessagesDelivered,
  mapSendResultToChatMessage,
  mapToMessageSendError,
  materializePickerFile,
  normalizeOutboundImageFile,
  patchOptimisticImageUrl,
  preloadServerImageUrl,
  putOutboundImageBlob,
  readOutboundQueue,
  registerImageBlobPreview,
  releasePageAttachmentForSend,
  removeOutboundImageBlob,
  reportWidgetError,
  reportWidgetEvent,
  resolveGreetingCommitForSendAsync,
  markContextPopupDismissed,
  markGreetingOfferCommitted,
  acknowledgeLauncherGreetingPhrases,
  resolveOutboundQueueScopeKey,
  subscribeConnectivity,
  summarizeImageMessage,
  refreshWidgetSessionThread,
  stripTypingPlaceholder,
  takePageForSend,
  touchDialogPageActivity,
  updateWidgetSessionThreadId,
  reconcileMessages,
  hasNewInboundReply,
  shouldPreserveMessageListSnapshot,
  stabilizeMessageList,
  WidgetUploadError,
  type SendImageMessageFn,
  type SendMessageFn,
  type SendMessageRequest,
  type SendMessageResult,
} from '@teo/widget-core';

export { MAX_IMAGE_UPLOAD_BYTES } from '@teo/widget-core';

export const ACCEPTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
] as const;

/** Delay after successful send before showing typing (fast replies skip it). */
const TYPING_SHOW_DELAY_MS = 1_500;
/** Hide typing if no inbound reply while dots are visible. */
const TYPING_MAX_VISIBLE_MS = 12_000;

type SendCommit = Pick<
  SendMessageRequest,
  'history' | 'greetingId' | 'page' | 'messageId'
>;

export type ChatContextValue = {
  messages: ChatMessage[];
  send: (text: string) => Promise<void>;
  sendImage: (file: File) => Promise<void>;
  retry: (clientId: string) => Promise<void>;
  isUploadingImage: boolean;
  isTyping: boolean;
  /** Current thread id (session / first send); `null` until backend assigns one */
  activeThreadId: number | string | null;
  /** Manager display name from thread API (`company.ai_manager_name`). */
  managerName: string | null;
  setThreadMeta: (meta: { managerName?: string | null }) => void;
  setMessages: (messages: ChatMessage[]) => void;
  syncFromServer: (serverMessages: ChatMessage[]) => void;
};

const ChatContext = createContext<ChatContextValue | null>(null);

export type ChatProviderProps = {
  children: ReactNode;
  /** Controlled list (Storybook static states) */
  messages?: ChatMessage[];
  /** Controlled send — skips optimistic pipeline */
  onSend?: (text: string) => void;
  /** Live chat */
  initialMessages?: ChatMessage[];
  threadId?: number | string | null;
  sendMessage?: SendMessageFn;
  sendImageMessage?: SendImageMessageFn;
  apiBase?: string;
};

export function ChatProvider({
  children,
  messages: controlledMessages,
  onSend: controlledOnSend,
  initialMessages = [],
  threadId = null,
  sendMessage,
  sendImageMessage,
  apiBase,
}: ChatProviderProps) {
  const [internalMessages, setInternalMessages] = useState(initialMessages);
  const [activeThreadId, setActiveThreadId] = useState<number | string | null>(
    threadId ?? null,
  );
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [managerName, setManagerName] = useState<string | null>(null);
  const messages = controlledMessages ?? internalMessages;
  const inFlightRef = useRef(new Set<string>());
  const pendingFilesRef = useRef(new Map<string, File>());
  const previewUrlsRef = useRef(new Map<string, string>());
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const typingMaxTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Set inside message updaters; consumed in layout effect (no side effects in setState). */
  const typingDisarmRef = useRef(false);
  const flushQueueInFlightRef = useRef(false);
  const pendingServerSyncRef = useRef<ChatMessage[] | null>(null);

  const cancelTypingSchedule = useCallback(() => {
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    }
    if (typingMaxTimerRef.current) {
      clearTimeout(typingMaxTimerRef.current);
      typingMaxTimerRef.current = null;
    }
    setIsTyping(false);
  }, []);

  const scheduleTypingPlaceholder = useCallback(() => {
    if (controlledMessages !== undefined) return;
    typingDisarmRef.current = false;
    cancelTypingSchedule();
    typingTimerRef.current = setTimeout(() => {
      typingTimerRef.current = null;
      if (typingDisarmRef.current) return;
      setIsTyping(true);
      setInternalMessages((current) => markUserMessagesDelivered(current));
      logRealtime('typing', 'typing shown after send delay');
      typingMaxTimerRef.current = setTimeout(() => {
        typingMaxTimerRef.current = null;
        setIsTyping(false);
        logRealtime('typing', 'typing hidden — max visible timeout');
      }, TYPING_MAX_VISIBLE_MS);
    }, TYPING_SHOW_DELAY_MS);
  }, [controlledMessages, cancelTypingSchedule]);

  const setThreadMeta = useCallback(
    (meta: { managerName?: string | null }) => {
      if (controlledMessages !== undefined) return;
      if (meta.managerName !== undefined) {
        setManagerName(meta.managerName);
      }
    },
    [controlledMessages],
  );

  const resolveQueueScopeKey = useCallback((): string | null => {
    const session = getWidgetSession();
    if (!session?.visitorId) return null;
    return resolveOutboundQueueScopeKey(session.visitorId, session.companyId);
  }, []);

  const applySendSuccess = useCallback(
    async (
      clientId: string,
      result: SendMessageResult,
      kind: 'text' | 'image' = 'text',
    ) => {
      if ('queued' in result) {
        if (apiBase) {
          const refreshed = await refreshWidgetSessionThread(apiBase);
          if (refreshed) {
            setActiveThreadId(refreshed);
            logRealtime('chat', 'thread_id after queued send', {
              threadId: refreshed,
            });
          }
        }
        setInternalMessages((current) => {
          const existing = findMessageByClientId(current, clientId);
          if (!existing) {
            logSync(
              'send',
              'queued ack — optimistic row missing',
              { clientId },
              'warn',
            );
            return current;
          }
          const next = current.map((row) =>
            row.clientId === clientId || row.id === clientId
              ? { ...row, status: 'sent' as const }
              : row,
          );
          logSync('send', 'queued ack — marked sent (shape preserved)', {
            clientId,
            row: summarizeChatMessage(
              next.find((row) => row.clientId === clientId)!,
            ),
          });
          return next;
        });
        scheduleTypingPlaceholder();
        return;
      }

      const confirmed = mapSendResultToChatMessage(result, clientId, kind);
      if ('message' in result) {
        const newThreadId = result.message.thread_id;
        if (newThreadId) {
          if (!activeThreadId) {
            setActiveThreadId(newThreadId);
          }
          updateWidgetSessionThreadId(newThreadId);
        }
      }
      setInternalMessages((current) => {
        const next = confirmOptimisticMessage(current, clientId, confirmed);
        logSync('send', 'confirmOptimisticMessage applied', {
          clientId,
          kind,
          before: current.find((row) => row.clientId === clientId)
            ? summarizeChatMessage(
                current.find((row) => row.clientId === clientId)!,
              )
            : null,
          after: summarizeChatMessage(
            next.find((row) => row.clientId === clientId || row.id === clientId) ??
              confirmed,
          ),
        });
        return next;
      });
      scheduleTypingPlaceholder();
    },
    [activeThreadId, apiBase, scheduleTypingPlaceholder],
  );

  const applyServerMessages = useCallback(
    (
      serverMessages: ChatMessage[],
      options?: { preserveTypingSchedule?: boolean },
    ) => {
      if (controlledMessages !== undefined) return;
      setInternalMessages((current) => {
        const stripped = stripTypingPlaceholder(current);
        const merged = reconcileMessages(serverMessages, stripped);
        let piped = applyOutgoingStatusPipeline(merged, current);
        if (current.length === 0) {
          piped = markUserMessagesDelivered(piped);
        }
        const next = stabilizeMessageList(current, piped);
        const diff = diffMessageLists(current, next);
        const inboundReply = hasNewInboundReply(current, next);

        logSync('sync', `applyServerMessages pipeline — ${formatMessageListDiff(diff)}`, {
          serverCount: serverMessages.length,
          localCount: current.length,
          mergedCount: merged.length,
          pipedCount: piped.length,
          inboundReply,
          preserveTypingSchedule: Boolean(options?.preserveTypingSchedule),
          serverTail: serverMessages.slice(-3).map(summarizeChatMessage),
          ...diff,
        });

        // Disarm only on a real new bot/manager row — not on user-echo / no-op sync.
        // Also disarm when deferred sync (`preserveTypingSchedule`) carries the reply.
        // Microtask: avoid setState side effects inside the updater; safe if Strict Mode doubles.
        if (inboundReply) {
          typingDisarmRef.current = true;
          queueMicrotask(() => {
            cancelTypingSchedule();
            logRealtime('typing', 'typing hidden — new inbound bot/manager reply');
          });
        }

        if (shouldPreserveMessageListSnapshot(current, next)) {
          logSync('sync', 'skipped setState — render-equivalent list', {
            serverCount: serverMessages.length,
            localCount: current.length,
            inboundReply,
          });
          logRealtime('chat', 'syncFromServer skipped — render-equivalent list', {
            serverCount: serverMessages.length,
            localCount: current.length,
          });
          return current;
        }
        logImage('sync', 'stabilizeMessageList', {
          reusedRefs: diff.reusedRefs,
          total: next.length,
        });
        const imageRows = next
          .filter((m) => m.contentType === 'image')
          .map(summarizeImageMessage);
        logImage('sync', 'syncFromServer image rows', {
          serverCount: serverMessages.length,
          localCount: current.length,
          mergedCount: next.length,
          images: imageRows,
        });
        logRealtime('chat', 'syncFromServer', {
          serverCount: serverMessages.length,
          localCount: current.length,
          mergedCount: next.length,
          inboundReply,
        });
        logScroll('messages', 'syncFromServer applied', {
          serverCount: serverMessages.length,
          localCount: current.length,
          mergedCount: next.length,
          reusedRefs: diff.reusedRefs,
          listChanged: next.length !== current.length || diff.reusedRefs !== current.length,
        });
        return next;
      });
    },
    [controlledMessages, cancelTypingSchedule],
  );

  const flushPendingServerSyncIfReady = useCallback(() => {
    if (flushQueueInFlightRef.current || inFlightRef.current.size > 0) {
      return;
    }
    const pending = pendingServerSyncRef.current;
    if (!pending) return;
    pendingServerSyncRef.current = null;
    logSync('sync', 'applying deferred server snapshot', {
      serverCount: pending.length,
    });
    applyServerMessages(pending, { preserveTypingSchedule: true });
  }, [applyServerMessages]);

  const resolveThreadId = useCallback(async (): Promise<number | string | null> => {
    if (activeThreadId != null && Number(activeThreadId) > 0) {
      return activeThreadId;
    }

    if (apiBase) {
      const refreshed = await refreshWidgetSessionThread(apiBase);
      if (refreshed) {
        setActiveThreadId(refreshed);
        return refreshed;
      }
    }

    return null;
  }, [activeThreadId, apiBase]);

  const flushOutboundQueueNow = useCallback(async () => {
    if (controlledMessages !== undefined || !sendMessage) return;
    if (flushQueueInFlightRef.current) return;

    const scopeKey = resolveQueueScopeKey();
    if (!scopeKey) return;

    flushQueueInFlightRef.current = true;
    try {
      const result = await flushOutboundQueue({
        scopeKey,
        sendMessage,
        sendImageMessage,
        getThreadId: () => activeThreadId,
        resolveThreadId,
        onConfirmed: async (clientId, sendResult, kind, entry) => {
          logRealtime('queue', 'flushed message', { clientId, kind });
          await applySendSuccess(clientId, sendResult, kind);
          if (entry.greetingId) {
            const session = getWidgetSession();
            if (session?.slug && typeof window !== 'undefined') {
              const greetingTexts = (entry.history ?? [])
                .map((item) => item.payload)
                .filter((text): text is string => Boolean(text?.trim()));
              acknowledgeLauncherGreetingPhrases(session.slug, greetingTexts);
              markGreetingOfferCommitted(
                session.slug,
                window.location.pathname,
                entry.greetingId,
              );
              markContextPopupDismissed(
                session.slug,
                window.location.pathname,
              );
            }
          }
          if (kind === 'image') {
            previewUrlsRef.current.delete(clientId);
            pendingFilesRef.current.delete(clientId);
          }
        },
        onImageUploadComplete: (clientId, url) => {
          void preloadServerImageUrl(clientId, url).then(() => {
            setInternalMessages((current) => {
              const row = findMessageByClientId(current, clientId);
              return patchOptimisticImageUrl(
                current,
                clientId,
                url,
                row?.attachment?.fileName,
              );
            });
          });
        },
        onFailed: (clientId, error) => {
          logRealtime('queue', 'flush failed', { clientId, error }, 'warn');
        },
      });
      logRealtime('queue', 'flush complete', result);
    } finally {
      flushQueueInFlightRef.current = false;
      flushPendingServerSyncIfReady();
    }
  }, [
    activeThreadId,
    applySendSuccess,
    flushPendingServerSyncIfReady,
    applyServerMessages,
    controlledMessages,
    resolveQueueScopeKey,
    resolveThreadId,
    sendImageMessage,
    sendMessage,
  ]);

  const queueTextMessage = useCallback(
    (
      clientId: string,
      text: string,
      commit?: SendCommit,
    ) => {
      const scopeKey = resolveQueueScopeKey();
      if (!scopeKey) return;
      enqueueOutboundMessage(scopeKey, {
        clientId,
        text,
        threadId: activeThreadId,
        createdAt: new Date().toISOString(),
        history: commit?.history,
        greetingId: commit?.greetingId,
        page: commit?.page,
        messageId: commit?.messageId,
      });
      logRealtime('queue', 'message queued', {
        clientId,
        scopeKey,
        history: commit?.history?.length ?? 0,
        greetingId: commit?.greetingId,
        page: commit?.page?.pathname,
        messageId: commit?.messageId,
      });
    },
    [activeThreadId, resolveQueueScopeKey],
  );

  const queueImageMessage = useCallback(
    async (
      clientId: string,
      file: File,
      options?: {
        uploadedUrl?: string;
        createdAt?: string;
        page?: ProactiveGreetingPage;
        messageId?: string;
      },
    ) => {
      const scopeKey = resolveQueueScopeKey();
      if (!scopeKey) return;

      const createdAt =
        options?.createdAt ??
        readOutboundQueue(scopeKey).find((row) => row.clientId === clientId)
          ?.createdAt ??
        new Date().toISOString();

      if (options?.uploadedUrl) {
        enqueueOutboundMessage(scopeKey, {
          clientId,
          kind: 'image',
          text: options.uploadedUrl,
          threadId: activeThreadId,
          createdAt,
          fileName: file.name,
          mimeType: file.type || undefined,
          uploadedUrl: true,
          page: options.page,
          messageId: options.messageId,
        });
        await removeOutboundImageBlob(scopeKey, clientId);
      } else {
        enqueueOutboundMessage(scopeKey, {
          clientId,
          kind: 'image',
          text: '',
          threadId: activeThreadId,
          createdAt,
          fileName: file.name,
          mimeType: file.type || undefined,
          uploadedUrl: false,
          page: options?.page,
          messageId: options?.messageId,
        });
        await putOutboundImageBlob(scopeKey, clientId, file);
      }
      logRealtime('queue', 'image queued', {
        clientId,
        scopeKey,
        page: options?.page?.pathname,
      });
    },
    [activeThreadId, resolveQueueScopeKey],
  );

  useEffect(() => {
    return () => {
      cancelTypingSchedule();
      for (const url of previewUrlsRef.current.values()) {
        URL.revokeObjectURL(url);
      }
      previewUrlsRef.current.clear();
      pendingFilesRef.current.clear();
    };
  }, [cancelTypingSchedule]);

  useEffect(() => {
    if (threadId != null && Number(threadId) > 0) {
      setActiveThreadId(threadId);
    }
  }, [threadId]);

  useEffect(() => {
    if (controlledMessages !== undefined || !sendMessage) return;

    const scopeKey = resolveQueueScopeKey();
    if (!scopeKey) return;

    let cancelled = false;

    void (async () => {
      const queued = readOutboundQueue(scopeKey);
      if (queued.length === 0) return;

      const imagePreviews = new Map<string, string>();

      for (const entry of queued) {
        if (!isImageQueueEntry(entry)) continue;
        if (entry.uploadedUrl && entry.text.trim()) {
          imagePreviews.set(entry.clientId, entry.text);
          continue;
        }
        const file = await getOutboundImageBlob(
          scopeKey,
          entry.clientId,
          entry.fileName,
          entry.mimeType,
        );
        if (!file) continue;
        const previewUrl = URL.createObjectURL(file);
        pendingFilesRef.current.set(entry.clientId, file);
        previewUrlsRef.current.set(entry.clientId, previewUrl);
        registerImageBlobPreview(entry.clientId, previewUrl);
        imagePreviews.set(entry.clientId, previewUrl);
      }

      if (cancelled) return;

      setInternalMessages((current) => {
        let next = current;
        for (const entry of queued) {
          if (findMessageByClientId(next, entry.clientId)) continue;
          if (isImageQueueEntry(entry)) {
            const previewUrl = imagePreviews.get(entry.clientId);
            if (!previewUrl) continue;
            next = addOptimisticImageMessage(
              next,
              previewUrl,
              entry.clientId,
              entry.fileName,
              entry.createdAt,
            );
          } else {
            next = addOptimisticMessage(
              next,
              entry.text,
              entry.clientId,
              entry.createdAt,
            );
          }
        }
        return next;
      });

      if (isBrowserOnline()) {
        void flushOutboundQueueNow();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    controlledMessages,
    flushOutboundQueueNow,
    resolveQueueScopeKey,
    sendMessage,
  ]);

  useEffect(() => {
    if (controlledMessages !== undefined || !sendMessage) return;

    return subscribeConnectivity((online) => {
      logRealtime('connectivity', online ? 'online' : 'offline');
      if (online) {
        void flushOutboundQueueNow();
      }
    });
  }, [controlledMessages, flushOutboundQueueNow, sendMessage]);

  const setMessages = useCallback(
    (next: ChatMessage[]) => {
      if (controlledMessages !== undefined) return;
      setInternalMessages(next);
    },
    [controlledMessages],
  );

  const syncFromServer = useCallback(
    (serverMessages: ChatMessage[]) => {
      if (controlledMessages !== undefined) return;
      if (flushQueueInFlightRef.current || inFlightRef.current.size > 0) {
        pendingServerSyncRef.current = serverMessages;
        logSync('sync', 'deferred — in-flight send or queue flush', {
          serverCount: serverMessages.length,
          inFlight: [...inFlightRef.current],
          queueFlush: flushQueueInFlightRef.current,
        });
        logRealtime('chat', 'syncFromServer deferred (in-flight)', {
          serverCount: serverMessages.length,
          inFlightCount: inFlightRef.current.size,
        });
        return;
      }
      applyServerMessages(serverMessages);
    },
    [applyServerMessages, controlledMessages],
  );

  const dispatchSend = useCallback(
    async (text: string, existingClientId?: string) => {
      const trimmed = text.trim();
      if (!trimmed || !sendMessage) return;

      const clientId = existingClientId ?? createClientMessageId();
      if (inFlightRef.current.has(clientId)) return;

      inFlightRef.current.add(clientId);
      logSync('send', 'text/link dispatch start', {
        clientId,
        text: trimmed.slice(0, 120),
        threadId: activeThreadId,
        retry: Boolean(existingClientId),
      });

      const session = getWidgetSession();
      const sessionHasThread =
        session?.threadId != null && Number(session.threadId) > 0;
      const activeHasThread =
        activeThreadId != null && Number(activeThreadId) > 0;
      const isFirstDialog = !sessionHasThread && !activeHasThread;
      const sendThreadId = activeHasThread
        ? activeThreadId
        : sessionHasThread
          ? session!.threadId
          : activeThreadId;

      let commit: SendCommit | undefined;

      if (existingClientId) {
        const scopeKey = resolveQueueScopeKey();
        const queued = scopeKey
          ? readOutboundQueue(scopeKey).find(
              (row) => row.clientId === existingClientId,
            )
          : undefined;
        if (queued) {
          commit = {
            history: queued.history,
            greetingId: queued.greetingId,
            page: queued.page,
            messageId: queued.messageId,
          };
        } else {
          const page = await takePageForSend({
            slug: session?.slug,
            threadId: sendThreadId,
          });
          commit = {
            page,
            messageId: createWidgetMessageId(),
          };
        }
      } else {
        const resolved = await resolveGreetingCommitForSendAsync();
        const page = await takePageForSend({
          slug: session?.slug,
          threadId: sendThreadId,
        });
        const messageId = createWidgetMessageId();
        if (resolved?.history?.length) {
          commit = {
            history: resolved.history,
            greetingId: resolved.greetingId,
            page,
            messageId,
          };
          if (session?.slug) {
            acknowledgeLauncherGreetingPhrases(
              session.slug,
              resolved.history.map((item) => item.payload),
            );
          }
        } else {
          commit = { page, messageId };
        }
        logSync('send', isFirstDialog ? 'first dialog — history' : 'mid-thread — history', {
          clientId,
          history: commit.history?.length ?? 0,
          greetingId: commit.greetingId,
          page: commit.page?.pathname,
          messageId: commit.messageId,
          sessionHasThread,
          activeHasThread,
        });
      }

      const messageId = commit?.messageId ?? createWidgetMessageId();
      commit = { ...commit, messageId };

      if (existingClientId) {
        setInternalMessages((current) => {
          const next = markMessageSending(current, existingClientId);
          logSync('send', 'retry → sending', {
            clientId,
            row: next.find((row) => row.clientId === existingClientId),
          });
          return next;
        });
      } else {
        setInternalMessages((current) => {
          const next = addOptimisticMessage(current, trimmed, clientId);
          const row = next.find((message) => message.clientId === clientId);
          logSync('send', 'optimistic row added', {
            clientId,
            row: row ? summarizeChatMessage(row) : null,
            listCount: next.length,
          });
          return next;
        });
      }

      if (!isBrowserOnline()) {
        queueTextMessage(clientId, trimmed, commit);
        touchDialogPageActivity({
          slug: session?.slug,
          threadId: sendThreadId,
        });
        inFlightRef.current.delete(clientId);
        return;
      }

      try {
        const result = await sendMessage({
          threadId: sendThreadId ?? null,
          text: trimmed,
          clientId,
          history: commit?.history,
          greetingId: commit?.greetingId,
          page: commit?.page,
          messageId,
        });
        logSync('send', 'POST /api/widget/send response', {
          clientId,
          queued: 'queued' in result,
          messageId: 'message' in result ? result.message.id : undefined,
          apiType: 'message' in result ? result.message.type : undefined,
          apiMessage: 'message' in result ? result.message.message?.slice(0, 120) : undefined,
          rawType:
            'message' in result ? result.message.raw_message?.type : undefined,
          rawPayload:
            'message' in result
              ? result.message.raw_message?.payload?.slice(0, 120)
              : undefined,
        });
        await applySendSuccess(clientId, result, 'text');
        const resolvedThreadId =
          'message' in result && result.message.thread_id
            ? result.message.thread_id
            : sendThreadId;
        touchDialogPageActivity({
          slug: session?.slug,
          threadId: resolvedThreadId,
        });
        if (commit?.greetingId && session?.slug && typeof window !== 'undefined') {
          const greetingTexts = (commit.history ?? [])
            .map((item) => item.payload)
            .filter((text): text is string => Boolean(text?.trim()));
          acknowledgeLauncherGreetingPhrases(session.slug, greetingTexts);
          markGreetingOfferCommitted(
            session.slug,
            window.location.pathname,
            commit.greetingId,
          );
          markContextPopupDismissed(session.slug, window.location.pathname);
        }
        reportWidgetEvent('message_sent', {
          message_length: trimmed.length,
          has_thread: sessionHasThread || activeHasThread,
          thread_id:
            sendThreadId != null && Number(sendThreadId) > 0
              ? Number(sendThreadId)
              : null,
        });
      } catch (error) {
        if (isNetworkError(error)) {
          queueTextMessage(clientId, trimmed, commit);
          touchDialogPageActivity({
            slug: session?.slug,
            threadId: sendThreadId,
          });
        } else {
          if (commit?.page?.pathname) {
            releasePageAttachmentForSend(commit.page.pathname, {
              slug: session?.slug,
            });
          }
          reportWidgetError(error, {
            source: 'send',
            fatal: false,
          });
          setInternalMessages((current) =>
            markMessageFailed(current, clientId, mapToMessageSendError(error)),
          );
        }
      } finally {
        inFlightRef.current.delete(clientId);
        flushPendingServerSyncIfReady();
      }
    },
    [
      sendMessage,
      activeThreadId,
      applySendSuccess,
      queueTextMessage,
      flushPendingServerSyncIfReady,
    ],
  );

  const dispatchSendImage = useCallback(
    async (file: File, existingClientId?: string) => {
      if (!sendImageMessage) return;

      const clientId = existingClientId ?? createClientMessageId();
      if (inFlightRef.current.has(clientId)) return;

      inFlightRef.current.add(clientId);
      setIsUploadingImage(true);

      // iOS Safari can revoke gallery File bytes after picker close / input clear.
      // Snapshot into the JS heap before any other await (normalize, thread, upload).
      try {
        file = await materializePickerFile(file);
        if (file.size === 0) {
          throw new WidgetUploadError(
            'UPLOAD_FAILED',
            '[Teo Widget] Image file is empty',
          );
        }
      } catch (error) {
        const sendError = mapToMessageSendError(
          error instanceof WidgetUploadError
            ? error
            : new WidgetUploadError(
                'UPLOAD_FAILED',
                '[Teo Widget] Could not read image file',
              ),
        );
        const previewUrl =
          previewUrlsRef.current.get(clientId) ?? URL.createObjectURL(file);
        previewUrlsRef.current.set(clientId, previewUrl);
        registerImageBlobPreview(clientId, previewUrl);
        pendingFilesRef.current.set(clientId, file);
        setInternalMessages((current) =>
          markMessageFailed(
            existingClientId
              ? current
              : addOptimisticImageMessage(
                  current,
                  previewUrl,
                  clientId,
                  file.name,
                ),
            clientId,
            sendError,
          ),
        );
        reportWidgetError(error, { source: 'upload', fatal: false });
        inFlightRef.current.delete(clientId);
        setIsUploadingImage(false);
        flushPendingServerSyncIfReady();
        return;
      }

      let previewUrl = previewUrlsRef.current.get(clientId);
      if (!previewUrl) {
        previewUrl = URL.createObjectURL(file);
        previewUrlsRef.current.set(clientId, previewUrl);
      }
      registerImageBlobPreview(clientId, previewUrl);

      const failBeforeUpload = (error: unknown) => {
        const sendError = mapToMessageSendError(error);
        pendingFilesRef.current.set(clientId, file);
        if (existingClientId) {
          setInternalMessages((current) =>
            markMessageFailed(current, existingClientId, sendError),
          );
        } else {
          setInternalMessages((current) =>
            markMessageFailed(
              addOptimisticImageMessage(
                current,
                previewUrl!,
                clientId,
                file.name,
              ),
              clientId,
              sendError,
            ),
          );
        }
        logImage('send', 'failed before upload', { clientId, error }, 'error');
        reportWidgetError(error, {
          source: 'upload',
          fatal: false,
        });
      };

      let workingFile: File;
      try {
        workingFile = await normalizeOutboundImageFile(file);
      } catch (error) {
        failBeforeUpload(error);
        inFlightRef.current.delete(clientId);
        setIsUploadingImage(false);
        flushPendingServerSyncIfReady();
        return;
      }

      // Prefer JPEG (or normalized) blob for preview — Chrome cannot paint HEIC in <img>.
      if (workingFile !== file || workingFile.type !== file.type) {
        const previousPreview = previewUrl;
        previewUrl = URL.createObjectURL(workingFile);
        previewUrlsRef.current.set(clientId, previewUrl);
        registerImageBlobPreview(clientId, previewUrl);
        if (previousPreview.startsWith('blob:')) {
          URL.revokeObjectURL(previousPreview);
        }
      }

      pendingFilesRef.current.set(clientId, workingFile);

      logImage('send', 'start', {
        clientId,
        fileName: workingFile.name,
        size: workingFile.size,
        mime: workingFile.type,
        preview: previewUrl.slice(0, 32),
      });

      if (existingClientId) {
        setInternalMessages((current) =>
          markMessageSending(current, existingClientId),
        );
      } else {
        setInternalMessages((current) =>
          addOptimisticImageMessage(
            current,
            previewUrl!,
            clientId,
            workingFile.name,
          ),
        );
        logImage('send', 'optimistic bubble added', { clientId });
      }

      let page: ProactiveGreetingPage | undefined;
      let messageId: string | undefined;
      if (existingClientId) {
        const scopeKey = resolveQueueScopeKey();
        const queued = scopeKey
          ? readOutboundQueue(scopeKey).find(
              (row) => row.clientId === existingClientId,
            )
          : undefined;
        page = queued?.page;
        messageId = queued?.messageId;
        if (!page) {
          page = await takePageForSend({
            slug: getWidgetSession()?.slug,
            threadId: activeThreadId,
          });
        }
      } else {
        page = await takePageForSend({
          slug: getWidgetSession()?.slug,
          threadId: activeThreadId,
        });
      }
      messageId = messageId ?? createWidgetMessageId();

      if (!isBrowserOnline()) {
        await queueImageMessage(clientId, workingFile, { page, messageId });
        touchDialogPageActivity({
          slug: getWidgetSession()?.slug,
          threadId: activeThreadId,
        });
        inFlightRef.current.delete(clientId);
        flushPendingServerSyncIfReady();
        setIsUploadingImage(false);
        return;
      }

      let uploadedServerUrl: string | undefined;

      try {
        const threadIdForUpload = await resolveThreadId();
        if (!threadIdForUpload) {
          // Attach UI is gated on threadId; do not queue eternal `sending`.
          const noThreadError = new WidgetUploadError(
            'NO_THREAD',
            '[Teo Widget] thread_id is required before image upload',
          );
          pendingFilesRef.current.set(clientId, workingFile);
          setInternalMessages((current) =>
            markMessageFailed(
              current,
              clientId,
              mapToMessageSendError(noThreadError),
            ),
          );
          logImage('send', 'failed before upload', { clientId, error: noThreadError }, 'error');
          reportWidgetError(noThreadError, {
            source: 'upload',
            fatal: false,
          });
          if (page?.pathname) {
            releasePageAttachmentForSend(page.pathname, {
              slug: getWidgetSession()?.slug,
            });
          }
          return;
        }

        const result = await sendImageMessage({
          file: workingFile,
          threadId: threadIdForUpload,
          clientId,
          page,
          messageId,
          onUploadComplete: ({ url }) => {
            uploadedServerUrl = url;
            logImage('send', 'upload complete (server url)', {
              clientId,
              url: url.split('/').pop(),
            });
            void preloadServerImageUrl(clientId, url).then(() => {
              setInternalMessages((current) =>
                patchOptimisticImageUrl(
                  current,
                  clientId,
                  url,
                  workingFile.name,
                ),
              );
              logImage('send', 'state patched after preload', { clientId });
            });
          },
        });

        const confirmed = mapSendResultToChatMessage(result, clientId, 'image');
        if ('message' in result) {
          const newThreadId = result.message.thread_id;
          if (newThreadId && !activeThreadId) {
            setActiveThreadId(newThreadId);
            updateWidgetSessionThreadId(newThreadId);
          }
        }

        setInternalMessages((current) =>
          confirmOptimisticMessage(current, clientId, confirmed),
        );
        scheduleTypingPlaceholder();
        const imageThreadId =
          'message' in result && result.message.thread_id
            ? result.message.thread_id
            : threadIdForUpload;
        touchDialogPageActivity({
          slug: getWidgetSession()?.slug,
          threadId: imageThreadId,
        });
        logImage('send', 'send confirmed', {
          clientId,
          confirmed: summarizeImageMessage(confirmed),
        });

        previewUrlsRef.current.delete(clientId);
        pendingFilesRef.current.delete(clientId);
      } catch (error) {
        if (
          error instanceof WidgetUploadError &&
          error.code === 'NO_THREAD'
        ) {
          pendingFilesRef.current.set(clientId, workingFile);
          setInternalMessages((current) =>
            markMessageFailed(
              current,
              clientId,
              mapToMessageSendError(error),
            ),
          );
          logImage('send', 'failed', { clientId, error }, 'error');
          reportWidgetError(error, {
            source: 'upload',
            fatal: false,
          });
          if (page?.pathname) {
            releasePageAttachmentForSend(page.pathname, {
              slug: getWidgetSession()?.slug,
            });
          }
          return;
        }
        if (!isBrowserOnline() || isRetryableQueueError(error)) {
          if (uploadedServerUrl) {
            await queueImageMessage(clientId, workingFile, {
              uploadedUrl: uploadedServerUrl,
              page,
              messageId,
            });
          } else {
            await queueImageMessage(clientId, workingFile, { page, messageId });
          }
          touchDialogPageActivity({
            slug: getWidgetSession()?.slug,
            threadId: activeThreadId,
          });
          logImage('send', 'queued for retry', { clientId });
          return;
        }
        logImage('send', 'failed', { clientId, error }, 'error');
        reportWidgetError(error, {
          source: 'upload',
          fatal: false,
        });
        if (page?.pathname) {
          releasePageAttachmentForSend(page.pathname, {
            slug: getWidgetSession()?.slug,
          });
        }
        setInternalMessages((current) =>
          markMessageFailed(current, clientId, mapToMessageSendError(error)),
        );
        throw new Error('image-send-failed');
      } finally {
        inFlightRef.current.delete(clientId);
        setIsUploadingImage(false);
        flushPendingServerSyncIfReady();
      }
    },
    [
      sendImageMessage,
      activeThreadId,
      resolveThreadId,
      resolveQueueScopeKey,
      queueImageMessage,
      flushPendingServerSyncIfReady,
      scheduleTypingPlaceholder,
    ],
  );

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;

      if (controlledOnSend) {
        controlledOnSend(trimmed);
        return;
      }

      if (controlledMessages !== undefined) return;

      await dispatchSend(trimmed);
    },
    [controlledOnSend, controlledMessages, dispatchSend],
  );

  const sendImage = useCallback(
    async (file: File) => {
      if (controlledMessages !== undefined) return;
      await dispatchSendImage(file);
    },
    [controlledMessages, dispatchSendImage],
  );

  const retry = useCallback(
    async (clientId: string) => {
      if (controlledMessages !== undefined) return;

      const message = findMessageByClientId(messages, clientId);
      if (!message || message.status !== 'failed') return;

      if (message.contentType === 'image') {
        let file = pendingFilesRef.current.get(clientId);
        if (!file) {
          const scopeKey = resolveQueueScopeKey();
          if (scopeKey) {
            file =
              (await getOutboundImageBlob(
                scopeKey,
                clientId,
                message.attachment?.fileName,
              )) ?? undefined;
            if (file) {
              pendingFilesRef.current.set(clientId, file);
            }
          }
        }
        if (file) {
          await dispatchSendImage(file, clientId);
          return;
        }
      }

      await dispatchSend(message.text, clientId);
    },
    [
      controlledMessages,
      dispatchSend,
      dispatchSendImage,
      messages,
      resolveQueueScopeKey,
    ],
  );

  const value = useMemo<ChatContextValue>(
    () => ({
      messages,
      send,
      sendImage,
      retry,
      isUploadingImage,
      isTyping,
      activeThreadId,
      managerName,
      setThreadMeta,
      setMessages,
      syncFromServer,
    }),
    [messages, send, sendImage, retry, isUploadingImage, isTyping, activeThreadId, managerName, setThreadMeta, setMessages, syncFromServer],
  );

  return (
    <ChatContext.Provider value={value}>{children}</ChatContext.Provider>
  );
}

export function useChat(): ChatContextValue {
  const ctx = useContext(ChatContext);
  if (!ctx) {
    throw new Error('useChat must be used within ChatProvider');
  }
  return ctx;
}

export function useChatOptional(): ChatContextValue | null {
  return useContext(ChatContext);
}
